import { parse } from 'query-string';
import appendQuery from 'append-query';

import { SINGLE_SHOE_COOKIE } from 'constants/cookies';
import { RESET_STALE_PRODUCTS } from 'constants/reduxActions';
import { err, setError } from 'actions/errors';
import { generateSeoOptimizedData } from 'actions/seoOptimizedData';
import { setAndStoreCookie, setSessionCookies } from 'actions/session';
import { stripAppRoot } from 'history/AppRootUtils';
import { clearSavedFilters, updateSavedFilters } from 'actions/search';
import { receiveAndDeferSearchResponse, receiveSearchResponse, requestSearch, searchFailed } from 'actions/products';
import { makeQueryStringSearchTerm } from 'helpers';
import { ALL_REDIRECTED_KEY, breakdownNonZso, combineQueryParams, formatSavedFilters, searchRedirectedNoResultsUrl } from 'helpers/SearchUtils';
import { convertPageParamToUrlPath } from 'helpers/ClientUtils';
import { slashSearchProducts, zsoSearchProducts } from 'apis/calypso';
import { deleteFiltersFromOpal, saveFiltersToOpal } from 'apis/opal';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import marketplace from 'cfg/marketplace.json';
import { redirectTo } from 'actions/redirect';
import { blacklistedSearch } from 'actions/blacklistedSearch';
import { OOS_REDIRECTED_RE, ZSO_URL_RE } from 'common/regex';
import { evSearchPageView } from 'events/search';
import { selectCalypsoConfig } from 'selectors/environment';
import {
  cancellableDelay,
  classifyUpstreamResult,
  isRetryableDegradation,
  reportDegradation,
  SEARCH_RESULTS_RETRY_BACKOFF_MS,
  SEARCH_RESULTS_RETRY_LIMIT
} from 'helpers/upstreamDegradation';
import AppEnvironment from 'helpers/AppEnvironment';

const {
  search: { codesTo404, hasSingleShoes }
} = marketplace;

export function formatGenderValueForSearchPixel(v) {
  return v
    .toLowerCase()
    .replace(/^(wo)?men'?s$/, '$1men')
    .replace(/^kid('s)?$/, 'kids');
}

/** helper function for pulling facet data out of the state for the search pixel */
export function makeSearchPixelFacetDataFromState(state) {
  const ret = {};

  // eslint-disable-next-line camelcase
  const genderFacet = state.filters?.selected?.singleSelects?.txAttrFacet_Gender;
  if (genderFacet && genderFacet.length === 1) {
    ret.gender = formatGenderValueForSearchPixel(genderFacet[0]);
  }

  const singleSelectZc1 = state.filters?.selected?.singleSelects?.zc1;
  if (singleSelectZc1) {
    ret.subcategories = singleSelectZc1;
  }
  if (!ret.subcategories) {
    const facets = state.facets?.navigation;
    if (facets?.length > 0) {
      const zc1List = facets.find(f => f.facetField === 'zc1');
      if (zc1List) {
        ret.subcategories = zc1List.values.map(obj => obj.name);
      }
    }
  }

  if (ret.subcategories?.length === 1) {
    const category = ret.subcategories[0];
    if (!/\s/.test(category)) {
      ret.category = category.toLowerCase();
    }
  }

  return ret;
}

/**
 * Determines if a failed calypso call should 404
 */
export function translatePatronErrorCode(statusCode) {
  return codesTo404.indexOf(statusCode) >= 0 ? 404 : 500;
}

/**
 * Dispatches a custom event intended for the ZFGA stack to control the text of the header term.
 * @param {String} term to set the header search to.
 * @param {Document} doc The doucment to dispatch the event from.
 */
export function setHeaderSearchTerm(term, doc = document, EventConstructor = CustomEvent) {
  doc.dispatchEvent(new EventConstructor('marty_set_search', { detail: { searchTerm: term } }));
}

export const shouldRedirectToPdp = location => parse(location.search)?.term || OOS_REDIRECTED_RE.test(location.search);

/**
 * Fetch search results and facets from a slash route
 * @param  {object}   location                             location object
 * @param  {function} [searchProducts=slashSearchProducts] slashSearch api call function
 * @return {object}                                        promise
 */
export function fetchFromSearch({
  location,
  isFresh,
  limit = null,
  bypassCache = false,
  shouldAppendResults = false,
  searchProducts = slashSearchProducts,
  isSearchHappeningServerSide,
  isFilterSort = false,
  signal
}) {
  return (dispatch, getState) => {
    const appState = getState();
    const {
      filters: currentFilters,
      cookies,
      products: { productLimit }
    } = appState;
    const calypso = selectCalypsoConfig(appState);
    const { urlFilterMapping: filterCache, wasSaveFiltersToggled } = currentFilters;
    const path = stripAppRoot(location.pathname).replace('/filters', '');
    const completeUrl = path + location.search;
    const filterForPath = filterCache[completeUrl];
    const parsedParams = breakdownNonZso(location.pathname, location.search);

    if (filterForPath && !bypassCache && !wasSaveFiltersToggled) {
      return Promise.resolve();
    } else {
      dispatch(requestSearch({ url: completeUrl, isFresh }));
      const cleanLocation = Object.assign({}, location, { pathname: path });
      const criteria = {
        location: cleanLocation,
        page: parsedParams.page,
        limit: limit || productLimit
      };

      const runAttempt = () =>
        searchProducts({ calypso, criteria, cookies, state: appState, signal })
          .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
          .then(fetchErrorMiddleware)
          .then(response => {
            processReceivedSearchResponse(response, dispatch, getState, parsedParams, completeUrl, shouldAppendResults, isSearchHappeningServerSide);
          });

      return runDegradableSearch({ runAttempt, dispatch, isSearchHappeningServerSide, isFilterSort, signal });
    }
  };
}

/**
 * Runs a PLP primary search request attempt and, on the client, transparently retries a degraded
 * request with increasing backoff before surfacing a full-page error. Retrying is an internal detail
 * of the fetch action: callers just dispatch the thunk and await the returned promise, which resolves
 * once results load, the request is cancelled, the SSR loading state is rendered, or the
 * full-page error is dispatched.
 *
 * @param {() => Promise} runAttempt              performs one fetch + process; rejects on a degrade
 * @param {function}      dispatch
 * @param {boolean}       isSearchHappeningServerSide  true during SSR
 * @param {boolean}       isFilterSort            labels the failure metric (FilterSortApplication vs SearchResultsLoad)
 * @param {AbortSignal}   [signal]                aborts the in-flight attempt and any pending backoff
 * @returns {Promise<void>}
 *
 * Behaviour by branch:
 *  - cancelled (customer navigation): resolve silently — no metric, no error.
 *  - non-retryable degrade (4xx, parse failure): render the full-page error immediately with the real
 *    upstream status — a retry cannot change the outcome, and on the server this returns the correct
 *    non-2xx status rather than a loading state that never resolves.
 *  - retryable degrade during SSR: render the loading state (`searchFailed()`), emit one metric,
 *    keep a 2xx, and DO NOT retry — the client re-issues after hydration.
 *  - retryable degrade on the client, attempts remain: keep the loading state (`isLoading` stays
 *    true) and retry after the next backoff; abort during the wait abandons the loop silently.
 *  - retryable degrade on the client, attempts exhausted: render the full-page error.
 * Every degraded attempt emits exactly one failure metric + error record at the source.
 */
function runDegradableSearch({ runAttempt, dispatch, isSearchHappeningServerSide, isFilterSort, signal }) {
  const isServerRender = isSearchHappeningServerSide || !AppEnvironment.isClient;
  const operation = isFilterSort ? 'FilterSortApplication' : 'SearchResultsLoad';

  const renderErrorPage = error => dispatch(setError(err.GENERIC, error, translatePatronErrorCode(error?.status)));

  const attempt = tries =>
    runAttempt().catch(error => {
      if (classifyUpstreamResult(error) === 'cancelled') {
        return; // Request intentionally cancelled on navigation — do not render an error page.
      }
      // Exactly one ops failure + one Olympus error record per degraded attempt, at the source.
      reportDegradation({ service: 'Olympus', operation, region: 'PrimarySearch', error });

      // A non-retryable degradation (4xx, parse failure) will not change on a retry — render the
      // error page now, on both the server and the client.
      if (!isRetryableDegradation(error)) {
        renderErrorPage(error);
        return;
      }

      if (isServerRender) {
        // Render the loading state and keep a 2xx; the client re-issues after
        // hydration (which retries internally). Do NOT retry on the server.
        dispatch(searchFailed());
        return;
      }

      const nextTry = tries + 1;
      if (nextTry < SEARCH_RESULTS_RETRY_LIMIT) {
        // Keep showing the loading state (isLoading stays true) and retry after an increasing
        // backoff. A navigation during the wait aborts the signal and abandons the loop.
        return cancellableDelay(SEARCH_RESULTS_RETRY_BACKOFF_MS[Math.min(tries, SEARCH_RESULTS_RETRY_BACKOFF_MS.length - 1)], signal).then(() => {
          if (signal?.aborted) {
            return; // navigated away during backoff — abandon silently
          }
          return attempt(nextTry);
        });
      }

      // Every attempt remained degraded → render the full-page error with the real upstream status.
      renderErrorPage(error);
    });

  return attempt(0);
}

export function processReceivedSearchResponse(
  response,
  dispatch,
  getState,
  parsedParams,
  completeUrl,
  shouldAppendResults,
  isSearchHappeningServerSide = false
) {
  const originalTerm = response.originalTerm || parsedParams.originalTerm || '';

  // make sure we didn't do a term search
  if (!completeUrl?.includes('?term')) {
    const allRedirected = parsedParams[ALL_REDIRECTED_KEY];

    /*  no search results from a filtered search - redirect to a term search
     ** only redirect once, if url contains the redirected flag do not redirect again
     */
    if (response.results?.length === 0 && !allRedirected) {
      dispatch(redirectTo(searchRedirectedNoResultsUrl(originalTerm)));
      return;
    }
  }

  if (isSearchHappeningServerSide) {
    dispatch(receiveAndDeferSearchResponse({ response, parsedParams, completeUrl }));
    return;
  }

  if (response.blacklisted) {
    dispatch(blacklistedSearch(originalTerm));
    return;
  }

  const appState = getState();
  const {
    filters: { page }
  } = appState;

  const term = response.term || '';

  const { executedSearchUrl: originalExecutedSearchUrl } = response;
  let executedSearchUrl = ZSO_URL_RE.test(originalExecutedSearchUrl)
    ? originalExecutedSearchUrl
    : convertPageParamToUrlPath(originalExecutedSearchUrl);

  if (parsedParams.queryParams) {
    executedSearchUrl = appendQuery(executedSearchUrl, parsedParams.queryParams, { encodeComponents: false });
  }

  const seoOptimizedData = generateSeoOptimizedData(response);

  if (response?.singleShoeRedirect && hasSingleShoes) {
    const { cookies, customer: isRecognizedCustomer } = appState;
    const curRedirectCount = cookies[SINGLE_SHOE_COOKIE];
    const newCookieVal = isRecognizedCustomer && curRedirectCount ? parseInt(curRedirectCount, 10) + 1 : 1;
    if (newCookieVal < 4) {
      if (isRecognizedCustomer) {
        // expire cookie in a month
        const curDate = new Date();
        curDate.setDate(curDate.getDate() + 30);
        dispatch(setAndStoreCookie(SINGLE_SHOE_COOKIE, newCookieVal, curDate));
      }
      dispatch(redirectTo(response.singleShoeRedirect));
    }
  }

  const resp = {
    ...response,
    executedSearchUrl,
    originalTerm,
    seoOptimizedData,
    term,
    customerPreferences: response.customerPreferences || null,
    sort: getAppliedSort(parsedParams, response),
    url: completeUrl,
    page,
    shouldAppendResults
  };

  dispatch(receiveSearchResponse(resp));
  const state = getState();
  evSearchPageView(state);
  return response;
}

/**
 * Pulls the sort parameters off of the search response, if it exists, otherwise uses the sort provided by the input query.
 * @param  {Object} parsedParams   THe search parameters pulled from a location URL.
 * @param  {Object} searchResponse The slash search api response
 * @return {Object}                sort object with keys being the field and value being the direction
 */
function getAppliedSort(parsedParams, searchResponse) {
  if (searchResponse.sorts && searchResponse.sorts.length) {
    const sortVal = {};
    searchResponse.sorts.forEach(sortObj => (sortVal[sortObj.field] = sortObj.direction));
    return sortVal;
  }
  return parsedParams.sort;
}

/**
 * Fetch search results and facets from a ZSO
 * @param  {object}   location                      location object
 * @param  {function} [zsoSearch=zsoSearchProducts] zsoSearch api call function
 * @return {object}                                 promise
 */
export function fetchFromZso({
  location,
  isFresh,
  bypassCache = false,
  shouldAppendResults = false,
  zsoSearch = zsoSearchProducts,
  isSearchHappeningServerSide,
  isFilterSort = false,
  signal
}) {
  return (dispatch, getState) => {
    const appState = getState();
    const {
      filters: { urlFilterMapping: filterCache },
      cookies,
      products: { productLimit }
    } = appState;
    const calypso = selectCalypsoConfig(appState);

    const path = stripAppRoot(location.pathname);
    const query = parse(location.search);
    const completeUrl = path + location.search;
    const filterForPath = filterCache[completeUrl];

    const criteria = { path, query, limit: productLimit };

    if (filterForPath && !bypassCache) {
      dispatch({ type: RESET_STALE_PRODUCTS });
      return Promise.resolve();
    } else {
      dispatch(requestSearch({ url: completeUrl, isFresh }));
      const runAttempt = () =>
        zsoSearch(calypso, criteria, cookies, appState, undefined, signal)
          .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
          .then(fetchErrorMiddleware)
          .then(response => {
            processReceivedZsoResponse({ query, url: completeUrl, shouldAppendResults }, response, dispatch, getState);
            return response;
          });

      return runDegradableSearch({ runAttempt, dispatch, isSearchHappeningServerSide, isFilterSort, signal });
    }
  };
}

export function processReceivedZsoResponse({ query = {}, term, url, shouldAppendResults = false }, response, dispatch, getState) {
  const { t, ot } = query;
  term = term || makeQueryStringSearchTerm(t || ot || '');
  const executedSearchUrl = combineQueryParams(response.executedSearchUrl, query);
  const seoOptimizedData = generateSeoOptimizedData(response);

  const resp = {
    ...response,
    executedSearchUrl,
    term,
    seoOptimizedData,
    customerPreferences: response.customerPreferences || null,
    sort: getAppliedSort({ sort: {} }, response),
    url,
    shouldAppendResults
  };

  dispatch(receiveSearchResponse(resp));
  const state = getState();
  evSearchPageView(state);
}

export function saveFilters(excludeFilter, wasSavedCookie, saveCustomerFilters = saveFiltersToOpal) {
  return (dispatch, getState) => {
    const appState = getState();
    const {
      environmentConfig: {
        api: { opal }
      },
      filters
    } = appState;

    const organizedFilters = formatSavedFilters(filters, excludeFilter, wasSavedCookie);
    if (organizedFilters) {
      return saveCustomerFilters(opal, {
        savedsizes: filters.savedsizes,
        organizedFilters
      })
        .then(fetchErrorMiddleware)
        .then(response => {
          if (response?.id) {
            dispatch(updateSavedFilters(response, filters.savedsizes.filters));
          }
        });
    }
  };
}

export function deleteSavedFilters(id, deleteCustomerFilters = deleteFiltersFromOpal) {
  return (dispatch, getState) => {
    const appState = getState();
    const {
      environmentConfig: {
        api: { opal }
      }
    } = appState;

    if (id) {
      return deleteCustomerFilters(opal, id).then(() => {
        dispatch(clearSavedFilters());
      });
    }
  };
}
